// File Name: ContactPage.jsx

import React, { useState } from "react";
import axios from "axios";

import {
  Mail,
  Phone,
  MapPin,
  Clock3,
  ArrowRight,
  Sparkles,
  MessageSquare,
  Briefcase,
  Send,
  Star,
} from "lucide-react";

import { motion, AnimatePresence } from "framer-motion";

import { API } from "@/lib/site";

export default function ContactPage() {
  const [activeTab, setActiveTab] = useState("contact");

  // =====================================================
  // CONTACT FORM
  // =====================================================

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [company, setCompany] = useState("");
  const [phone, setPhone] = useState("");
  const [message, setMessage] = useState("");

  // =====================================================
  // CONSULTATION FORM
  // =====================================================

  const [consultName, setConsultName] = useState("");
  const [consultEmail, setConsultEmail] = useState("");
  const [consultPhone, setConsultPhone] = useState("");
  const [consultCompany, setConsultCompany] = useState("");
  const [consultService, setConsultService] = useState("");
  const [consultNotes, setConsultNotes] = useState("");

  // =====================================================
  // FEEDBACK FORM
  // =====================================================

  const [feedbackName, setFeedbackName] = useState("");
  const [feedbackEmail, setFeedbackEmail] = useState("");
  const [feedbackRating, setFeedbackRating] = useState(5);
  const [feedbackMessage, setFeedbackMessage] = useState("");

  const [loading, setLoading] = useState(false);

  // =====================================================
  // CONTACT SUBMIT
  // =====================================================

  const submitContactForm = async () => {
    if (!name || !email || !message) {
      alert("Please fill all required fields");
      return;
    }

    try {
      setLoading(true);

      await axios.post(`${API}/contact`, {
        name,
        email,
        company,
        phone,
        message,
      });

      alert("Message sent successfully!");

      setName("");
      setEmail("");
      setCompany("");
      setPhone("");
      setMessage("");
    } catch (err) {
      console.log(err);

      alert("Failed to send message");
    } finally {
      setLoading(false);
    }
  };

  // =====================================================
  // CONSULTATION SUBMIT
  // =====================================================

  const submitConsultationForm = async () => {
    if (
      !consultName ||
      !consultEmail ||
      !consultPhone ||
      !consultService
    ) {
      alert("Please fill all required fields");
      return;
    }

    try {
      setLoading(true);

      await axios.post(`${API}/consultation`, {
        name: consultName,
        email: consultEmail,
        phone: consultPhone,
        company: consultCompany,
        service: consultService,
        notes: consultNotes,
      });

      alert("Consultation booked successfully!");

      setConsultName("");
      setConsultEmail("");
      setConsultPhone("");
      setConsultCompany("");
      setConsultService("");
      setConsultNotes("");
    } catch (err) {
      console.log(err);

      alert("Failed to book consultation");
    } finally {
      setLoading(false);
    }
  };

  // =====================================================
  // FEEDBACK SUBMIT
  // =====================================================

  const submitFeedbackForm = async () => {
    if (
      !feedbackName ||
      !feedbackEmail ||
      !feedbackMessage
    ) {
      alert("Please fill all required fields");
      return;
    }

    try {
      setLoading(true);

      await axios.post(`${API}/feedback`, {
        name: feedbackName,
        email: feedbackEmail,
        rating: feedbackRating,
        feedback: feedbackMessage,
      });

      alert("Feedback submitted successfully!");

      setFeedbackName("");
      setFeedbackEmail("");
      setFeedbackRating(5);
      setFeedbackMessage("");
    } catch (err) {
      console.log(err);

      alert("Failed to submit feedback");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* GLOBAL STYLES */}

      <style>
        {`
          .contact-bg {
            background:
              radial-gradient(circle at top right, rgba(139,92,246,0.14), transparent 28%),
              radial-gradient(circle at bottom left, rgba(168,85,247,0.10), transparent 32%),
              linear-gradient(to bottom, #ffffff, #faf7ff);
          }

          .glass-card {
            background: rgba(255,255,255,0.88);
            border: 1px solid rgba(221,214,254,0.85);
            backdrop-filter: blur(18px);
            box-shadow:
              0 10px 40px rgba(139,92,246,0.08),
              0 4px 12px rgba(15,23,42,0.04);
          }

          .form-card {
            background: rgba(255,255,255,0.96);
            border: 1px solid rgba(221,214,254,0.8);
            box-shadow:
              0 8px 24px rgba(139,92,246,0.05);
          }

          .contact-input {
            width: 100%;
            border: 1px solid #DDD6FE;
            background: white;
            padding: 15px 18px;
            border-radius: 18px;
            outline: none;
            transition: all 0.3s ease;
            color: #0F172A;
            font-size: 14px;
          }

          .contact-input:focus {
            border-color: #8B5CF6;
            box-shadow:
              0 0 0 4px rgba(139,92,246,0.10);
          }

          .submit-btn {
            background:
              linear-gradient(
                135deg,
                #7C3AED,
                #A855F7
              );

            transition: all 0.3s ease;

            box-shadow:
              0 15px 35px rgba(139,92,246,0.20);
          }

          .submit-btn:hover {
            transform: translateY(-2px);

            box-shadow:
              0 20px 42px rgba(139,92,246,0.28);
          }

          .submit-btn:disabled {
            opacity: 0.7;
            cursor: not-allowed;
          }

          .info-panel {
            background:
              linear-gradient(
                180deg,
                #8B5CF6 0%,
                #7C3AED 100%
              );

            position: relative;
            overflow: hidden;

            box-shadow:
              0 24px 60px rgba(139,92,246,0.18);
          }

          .info-panel::before {
            content: "";

            position: absolute;

            width: 260px;
            height: 260px;

            border-radius: 999px;

            background:
              rgba(255,255,255,0.10);

            top: -90px;
            right: -90px;
          }

          .info-box {
            background:
              rgba(255,255,255,0.10);

            border:
              1px solid rgba(255,255,255,0.14);

            backdrop-filter: blur(12px);

            transition: all 0.3s ease;
          }

          .info-box:hover {
            background:
              rgba(255,255,255,0.16);

            transform: translateY(-2px);
          }

          .tab-button {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            gap: 10px;

            padding: 14px 20px;

            border-radius: 999px;

            border: 1px solid #DDD6FE;

            background: white;

            color: #0F172A;

            font-size: 14px;
            font-weight: 600;

            transition: all 0.3s ease;

            min-width: 180px;
          }

          .tab-button:hover {
            border-color: #8B5CF6;
            color: #7C3AED;
            transform: translateY(-2px);
          }

          .tab-button.active {
            background:
              linear-gradient(
                135deg,
                #7C3AED,
                #A855F7
              );

            color: white;

            border-color: transparent;

            box-shadow:
              0 12px 24px rgba(139,92,246,0.18);
          }

          .star-btn {
            background: transparent;
            border: none;
            cursor: pointer;
            transition: all 0.25s ease;
          }

          .star-btn:hover {
            transform: scale(1.10);
          }

          @media (max-width: 768px) {
            .tab-button {
              width: 100%;
              min-width: unset;
            }
          }
        `}
      </style>

      <section className="contact-bg relative overflow-hidden pt-28 md:pt-32 pb-20">

        {/* BACKGROUND GLOWS */}

        <div
          className="
            absolute
            top-0
            right-0
            w-[420px]
            h-[420px]
            bg-purple-300/20
            rounded-full
            blur-[110px]
          "
        />

        <div
          className="
            absolute
            bottom-0
            left-0
            w-[360px]
            h-[360px]
            bg-fuchsia-300/10
            rounded-full
            blur-[100px]
          "
        />

        {/* GRID */}

        <div
          className="
            absolute
            inset-0
            opacity-[0.03]
            bg-[linear-gradient(to_right,#7c3aed_1px,transparent_1px),linear-gradient(to_bottom,#7c3aed_1px,transparent_1px)]
            bg-[size:48px_48px]
          "
        />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 md:px-8">

          {/* HEADING */}

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto mb-14"
          >

            <div
              className="
                inline-flex
                items-center
                gap-2
                px-5
                py-2
                rounded-full
                border
                border-violet-200
                bg-white/80
                backdrop-blur-md
                text-violet-700
                text-xs
                md:text-sm
                font-semibold
                tracking-wide
                mb-6
              "
            >

              <Sparkles className="w-4 h-4" />

              CONTACT CODEFIX SOLUTIONS

            </div>

            <h1
              className="
                text-4xl
                sm:text-5xl
                md:text-6xl
                font-bold
                text-slate-900
                leading-tight
              "
              style={{ fontFamily: "Sora" }}
            >

              Let’s build something

              <span
                className="
                  block
                  bg-gradient-to-r
                  from-violet-700
                  via-fuchsia-500
                  to-purple-500
                  bg-clip-text
                  text-transparent
                "
              >

                exceptional together

              </span>

            </h1>

            <p
              className="
                mt-6
                text-base
                md:text-lg
                text-slate-600
                leading-relaxed
              "
            >

              Reach out to our team for enterprise software,
              AI-powered systems, staffing solutions,
              cybersecurity consulting and digital transformation.

            </p>

          </motion.div>

          {/* MAIN GRID */}

          <div
            className="
              grid
              grid-cols-1
              xl:grid-cols-[1fr_0.75fr]
              gap-8
              items-start
            "
          >

            {/* LEFT SIDE */}

            <div className="space-y-6">

              {/* TAB BUTTONS */}

              <div
                className="
                  flex
                  flex-col
                  sm:flex-row
                  flex-wrap
                  gap-4
                "
              >

                <button
                  onClick={() => setActiveTab("contact")}
                  className={`tab-button ${
                    activeTab === "contact"
                      ? "active"
                      : ""
                  }`}
                >

                  <Send className="w-4 h-4" />

                  Send Message

                </button>

                <button
                  onClick={() =>
                    setActiveTab("consultation")
                  }
                  className={`tab-button ${
                    activeTab === "consultation"
                      ? "active"
                      : ""
                  }`}
                >

                  <Briefcase className="w-4 h-4" />

                  Consultation

                </button>

                <button
                  onClick={() =>
                    setActiveTab("feedback")
                  }
                  className={`tab-button ${
                    activeTab === "feedback"
                      ? "active"
                      : ""
                  }`}
                >

                  <MessageSquare className="w-4 h-4" />

                  Feedback

                </button>

              </div>

              {/* FORM CARD */}

              <div className="glass-card rounded-[32px] p-4 sm:p-6 md:p-7">

                <AnimatePresence mode="wait">

                  {/* CONTACT FORM */}

                  {activeTab === "contact" && (

                    <motion.div
                      key="contact"
                      initial={{
                        opacity: 0,
                        y: 20,
                      }}
                      animate={{
                        opacity: 1,
                        y: 0,
                      }}
                      exit={{
                        opacity: 0,
                        y: -10,
                      }}
                      transition={{
                        duration: 0.35,
                      }}
                      className="form-card rounded-[28px] p-5 sm:p-7"
                    >

                      <h2
                        className="
                          text-3xl
                          md:text-4xl
                          font-bold
                          text-slate-900
                          mb-7
                        "
                        style={{ fontFamily: "Sora" }}
                      >

                        Contact Our Team

                      </h2>

                      <div
                        className="
                          grid
                          grid-cols-1
                          md:grid-cols-2
                          gap-5
                          mb-5
                        "
                      >

                        <input
                          type="text"
                          placeholder="Full Name"
                          className="contact-input"
                          value={name}
                          onChange={(e) =>
                            setName(e.target.value)
                          }
                        />

                        <input
                          type="email"
                          placeholder="Email Address"
                          className="contact-input"
                          value={email}
                          onChange={(e) =>
                            setEmail(e.target.value)
                          }
                        />

                        <input
                          type="text"
                          placeholder="Company Name"
                          className="contact-input"
                          value={company}
                          onChange={(e) =>
                            setCompany(e.target.value)
                          }
                        />

                        <input
                          type="text"
                          placeholder="Phone Number"
                          className="contact-input"
                          value={phone}
                          onChange={(e) =>
                            setPhone(e.target.value)
                          }
                        />

                      </div>

                      <textarea
                        rows="6"
                        placeholder="Tell us about your project..."
                        className="contact-input resize-none mb-6"
                        value={message}
                        onChange={(e) =>
                          setMessage(e.target.value)
                        }
                      />

                      <button
                        onClick={submitContactForm}
                        disabled={loading}
                        className="
                          submit-btn
                          w-full
                          sm:w-auto
                          inline-flex
                          items-center
                          justify-center
                          gap-2
                          px-7
                          py-4
                          rounded-2xl
                          text-white
                          font-semibold
                        "
                      >

                        {loading
                          ? "Sending..."
                          : "Send Message"}

                        <ArrowRight className="w-4 h-4" />

                      </button>

                    </motion.div>
                  )}

                  {/* CONSULTATION FORM */}

                  {activeTab === "consultation" && (

                    <motion.div
                      key="consultation"
                      initial={{
                        opacity: 0,
                        y: 20,
                      }}
                      animate={{
                        opacity: 1,
                        y: 0,
                      }}
                      exit={{
                        opacity: 0,
                        y: -10,
                      }}
                      transition={{
                        duration: 0.35,
                      }}
                      className="form-card rounded-[28px] p-5 sm:p-7"
                    >

                      <h2
                        className="
                          text-3xl
                          md:text-4xl
                          font-bold
                          text-slate-900
                          mb-7
                        "
                        style={{ fontFamily: "Sora" }}
                      >

                        Book Consultation

                      </h2>

                      <div
                        className="
                          grid
                          grid-cols-1
                          md:grid-cols-2
                          gap-5
                          mb-5
                        "
                      >

                        <input
                          type="text"
                          placeholder="Full Name"
                          className="contact-input"
                          value={consultName}
                          onChange={(e) =>
                            setConsultName(
                              e.target.value
                            )
                          }
                        />

                        <input
                          type="email"
                          placeholder="Email Address"
                          className="contact-input"
                          value={consultEmail}
                          onChange={(e) =>
                            setConsultEmail(
                              e.target.value
                            )
                          }
                        />

                        <input
                          type="text"
                          placeholder="Phone Number"
                          className="contact-input"
                          value={consultPhone}
                          onChange={(e) =>
                            setConsultPhone(
                              e.target.value
                            )
                          }
                        />

                        <input
                          type="text"
                          placeholder="Company Name"
                          className="contact-input"
                          value={consultCompany}
                          onChange={(e) =>
                            setConsultCompany(
                              e.target.value
                            )
                          }
                        />

                      </div>

                      <input
                        type="text"
                        placeholder="Service Required"
                        className="contact-input mb-5"
                        value={consultService}
                        onChange={(e) =>
                          setConsultService(
                            e.target.value
                          )
                        }
                      />

                      <textarea
                        rows="6"
                        placeholder="Additional Notes..."
                        className="contact-input resize-none mb-6"
                        value={consultNotes}
                        onChange={(e) =>
                          setConsultNotes(
                            e.target.value
                          )
                        }
                      />

                      <button
                        onClick={submitConsultationForm}
                        disabled={loading}
                        className="
                          submit-btn
                          w-full
                          sm:w-auto
                          inline-flex
                          items-center
                          justify-center
                          gap-2
                          px-7
                          py-4
                          rounded-2xl
                          text-white
                          font-semibold
                        "
                      >

                        {loading
                          ? "Booking..."
                          : "Book Consultation"}

                        <ArrowRight className="w-4 h-4" />

                      </button>

                    </motion.div>
                  )}

                  {/* FEEDBACK FORM */}

                  {activeTab === "feedback" && (

                    <motion.div
                      key="feedback"
                      initial={{
                        opacity: 0,
                        y: 20,
                      }}
                      animate={{
                        opacity: 1,
                        y: 0,
                      }}
                      exit={{
                        opacity: 0,
                        y: -10,
                      }}
                      transition={{
                        duration: 0.35,
                      }}
                      className="form-card rounded-[28px] p-5 sm:p-7"
                    >

                      <h2
                        className="
                          text-3xl
                          md:text-4xl
                          font-bold
                          text-slate-900
                          mb-7
                        "
                        style={{ fontFamily: "Sora" }}
                      >

                        Share Feedback

                      </h2>

                      <div
                        className="
                          grid
                          grid-cols-1
                          md:grid-cols-2
                          gap-5
                          mb-5
                        "
                      >

                        <input
                          type="text"
                          placeholder="Full Name"
                          className="contact-input"
                          value={feedbackName}
                          onChange={(e) =>
                            setFeedbackName(
                              e.target.value
                            )
                          }
                        />

                        <input
                          type="email"
                          placeholder="Email Address"
                          className="contact-input"
                          value={feedbackEmail}
                          onChange={(e) =>
                            setFeedbackEmail(
                              e.target.value
                            )
                          }
                        />

                      </div>

                      <div className="mb-6">

                        <div
                          className="
                            text-slate-700
                            font-semibold
                            mb-4
                          "
                        >

                          Rate Your Experience

                        </div>

                        <div className="flex gap-2">

                          {[1, 2, 3, 4, 5].map(
                            (star) => (

                              <button
                                key={star}
                                onClick={() =>
                                  setFeedbackRating(
                                    star
                                  )
                                }
                                className="star-btn"
                              >

                                <Star
                                  className={`w-8 h-8 ${
                                    star <=
                                    feedbackRating
                                      ? "fill-orange-400 text-orange-400"
                                      : "text-slate-300"
                                  }`}
                                />

                              </button>
                            )
                          )}

                        </div>

                      </div>

                      <textarea
                        rows="6"
                        placeholder="Write your feedback..."
                        className="contact-input resize-none mb-6"
                        value={feedbackMessage}
                        onChange={(e) =>
                          setFeedbackMessage(
                            e.target.value
                          )
                        }
                      />

                      <button
                        onClick={submitFeedbackForm}
                        disabled={loading}
                        className="
                          submit-btn
                          w-full
                          sm:w-auto
                          inline-flex
                          items-center
                          justify-center
                          gap-2
                          px-7
                          py-4
                          rounded-2xl
                          text-white
                          font-semibold
                        "
                      >

                        {loading
                          ? "Submitting..."
                          : "Submit Feedback"}

                        <ArrowRight className="w-4 h-4" />

                      </button>

                    </motion.div>
                  )}

                </AnimatePresence>

              </div>

            </div>

            {/* RIGHT PANEL */}

            <motion.div
              initial={{
                opacity: 0,
                x: 20,
              }}
              animate={{
                opacity: 1,
                x: 0,
              }}
              transition={{
                duration: 0.6,
              }}
              className="
                info-panel
                rounded-[32px]
                p-6
                md:p-8
                text-white
                xl:sticky
                xl:top-24
              "
            >

              <div className="relative z-10">

                <div
                  className="
                    inline-flex
                    items-center
                    gap-2
                    px-4
                    py-2
                    rounded-full
                    border
                    border-white/15
                    bg-white/10
                    text-sm
                    mb-6
                  "
                >

                  <Sparkles className="w-4 h-4" />

                  Available for Projects

                </div>

                <h2
                  className="
                    text-3xl
                    md:text-4xl
                    font-bold
                    leading-tight
                    mb-5
                  "
                  style={{ fontFamily: "Sora" }}
                >

                  Let’s talk about your next big idea.

                </h2>

                <p
                  className="
                    text-purple-50
                    leading-relaxed
                    mb-8
                    text-sm
                    md:text-base
                  "
                >

                  Our experts help businesses build scalable
                  software systems, enterprise platforms,
                  cybersecurity architecture and AI-driven
                  solutions.

                </p>

                <div className="space-y-4">

                  <div
                    className="
                      info-box
                      rounded-2xl
                      p-5
                      flex
                      items-start
                      gap-4
                    "
                  >

                    <Mail className="w-5 h-5 shrink-0 mt-0.5" />

                    <div>

                      <div className="font-semibold">
                        Email Address
                      </div>

                      <div className="text-sm text-purple-50 mt-1 break-all">
                        codefixsolutions01@gmail.com
                      </div>

                    </div>

                  </div>

                  <div
                    className="
                      info-box
                      rounded-2xl
                      p-5
                      flex
                      items-start
                      gap-4
                    "
                  >

                    <Phone className="w-5 h-5 shrink-0 mt-0.5" />

                    <div>

                      <div className="font-semibold">
                        Phone Number
                      </div>

                      <div className="text-sm text-purple-50 mt-1">
                        +91 7075392825
                      </div>

                    </div>

                  </div>

                  <div
                    className="
                      info-box
                      rounded-2xl
                      p-5
                      flex
                      items-start
                      gap-4
                    "
                  >

                    <MapPin className="w-5 h-5 shrink-0 mt-0.5" />

                    <div>

                      <div className="font-semibold">
                        Office Location
                      </div>

                      <div className="text-sm text-purple-50 mt-1">
                        Hyderabad, India
                      </div>

                    </div>

                  </div>

                  <div
                    className="
                      info-box
                      rounded-2xl
                      p-5
                      flex
                      items-start
                      gap-4
                    "
                  >

                    <Clock3 className="w-5 h-5 shrink-0 mt-0.5" />

                    <div>

                      <div className="font-semibold">
                        Working Hours
                      </div>

                      <div className="text-sm text-purple-50 mt-1">
                        Monday - Friday • 9AM - 7PM
                      </div>

                    </div>

                  </div>

                </div>

              </div>

            </motion.div>

          </div>

        </div>

      </section>
    </>
  );
}